import {
  Building2,
  Users,
  Wallet,
  TrendingUp,
  Briefcase,
  Heart,
} from "lucide-react";
import {
  PieChart,
  Pie,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
} from "recharts";
import { SECTOR_LABELS } from "../../data/mockData";
import { ROLES } from "../../constants/roles";
import { useAuth } from "../../context/authContext";
import { useDashboardHook } from "../../hooks/useDashboardHook";
import {
  Card,
  PageHeader,
  StatCard,
  CHART_COLORS,
} from "../fundercomponents/funderComponents";
import "./dashboard.css";

export default function Dashboard() {
  const { token, user } = useAuth();
  const normalizedRole = String(user?.role ?? "").toLowerCase();
  const roleAllowed = normalizedRole === ROLES.FUNDER;

  const {
    data: apiData,
    isLoading,
    error: queryError,
  } = useDashboardHook({
    token,
    role: user?.role,
    userId: user?.id,
    enabled: roleAllowed,
  });

  const toNumber = (value) => {
    const parsed = Number(String(value ?? "0").replace(/[^\d.-]/g, ""));
    return Number.isFinite(parsed) ? parsed : 0;
  };

  const metricMap = (items = []) =>
    Object.fromEntries(items.map((item) => [item.metric, item.value]));

  const financial = metricMap(apiData?.metrics?.financial);
  const growth = metricMap(apiData?.metrics?.survival_and_growth);

  // --- Aggregate stats
  const totalVentures = toNumber(apiData?.ventures_supported?.value);
  const ventureTrend = toNumber(apiData?.ventures_supported?.trend?.value);
  const womenLedPercent = toNumber(
    apiData?.women_led_ventures?.portfolio_percent,
  );

  const totalCapitalIH =
    apiData?.capital_deployed?.imagineher?.value ?? "UGX 0";
  const totalExternalCapital =
    apiData?.capital_deployed?.external?.value ?? "UGX 0";
  const leverageRatio =
    apiData?.capital_deployed?.external?.leverage ??
    financial["Capital Leverage"] ??
    "0x";
  const totalJobs = toNumber(apiData?.jobs_created?.value);
  const totalBeneficiaries = toNumber(apiData?.beneficiaries?.value);

  const averageTicketSize = financial["Avg. Ticket Size"] ?? "UGX 0";
  const repaymentRate = financial["Repayment Rate"] ?? "0%";
  const portfolioRevenue = financial["Portfolio Revenue"] ?? "UGX 0";

  // --- Chart datasets from backend
  const sectorData = (apiData?.ventures_by?.sector ?? []).map((row, index) => ({
    name: SECTOR_LABELS[row.sector?.toLowerCase()] ?? row.sector,
    value: toNumber(row.count),
    fill: CHART_COLORS[index % CHART_COLORS.length],
  }));

  const genderData = (apiData?.gender_classifications ?? []).map((row) => ({
    name: row.classification,
    value: toNumber(row.count),
  }));

  const qolRadarData = (apiData?.metrics?.quality_of_life ?? []).map(
    (item) => ({
      indicator: item.metric,
      value: toNumber(item.value),
      fullMark: 5,
    }),
  );

  const avgQoL =
    qolRadarData.length > 0
      ? qolRadarData.reduce((sum, item) => sum + item.value, 0) /
        qolRadarData.length
      : 0;

  const survival6Months = growth["6-Month Survival"] ?? "0%";
  const survival12Months = growth["12-Month Survival"] ?? "0%";
  const qolImprovement = growth["Avg. QoL Improvement"] ?? "0%";
  const graduatedCount = growth["Graduated Ventures"] ?? "0";

  if (isLoading) {
    return (
      <div className="funderDashboard">
        <PageHeader
          title="Impact & Accountability Dashboard"
          description="Loading dashboard data..."
        />
      </div>
    );
  }

  if (!roleAllowed) {
    return (
      <div className="funderDashboard">
        <PageHeader
          title="Impact & Accountability Dashboard"
          description="This dashboard is only available to funder accounts."
        />
      </div>
    );
  }

  if (!token) {
    return (
      <div className="funderDashboard">
        <PageHeader
          title="Impact & Accountability Dashboard"
          description="Missing auth token. Please sign in again."
        />
      </div>
    );
  }

  if (queryError) {
    return (
      <div className="funderDashboard">
        <PageHeader
          title="Impact & Accountability Dashboard"
          description={queryError.message || "Unable to load dashboard data."}
        />
      </div>
    );
  }

  return (
    <div className="funderDashboard">
      <PageHeader
        title="Impact & Accountability Dashboard"
        description="Aggregated portfolio performance and outcomes."
      />

      {/* Top KPI row */}
      <section className="funderDashboard-kpis" aria-label="Headline KPIs">
        <StatCard
          icon={Building2}
          title="Ventures Supported"
          value={totalVentures.toString()}
          trend={{ value: ventureTrend, isPositive: ventureTrend >= 0 }}
        />
        <StatCard
          icon={Users}
          title="Women-Led"
          value={`${womenLedPercent}%`}
          description="of portfolio"
        />
        <StatCard
          icon={Wallet}
          title="Capital Deployed"
          value={totalCapitalIH}
        />
        <StatCard
          icon={TrendingUp}
          title="External Capital"
          value={totalExternalCapital}
          description={`${leverageRatio} leverage`}
        />
        <StatCard
          icon={Briefcase}
          title="Jobs Created"
          value={totalJobs.toString()}
        />
        <StatCard
          icon={Heart}
          title="End Customers"
          value={totalBeneficiaries.toLocaleString()}
        />
      </section>

      {/* Composition: sector + gender */}
      <div className="funderDashboard-grid-2">
        <Card
          title="Ventures by Sector"
          description="Distribution across focus areas."
        >
          <div className="funderDashboard-chart">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={sectorData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={100}
                  paddingAngle={2}
                  dataKey="value"
                  label={({ name, value }) => `${name}: ${value}`}
                  labelLine={false}
                />
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card
          title="Gender Classification"
          description="Leadership demographics."
        >
          <div className="funderDashboard-chart">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={genderData} layout="vertical">
                <XAxis type="number" allowDecimals={false} />
                <YAxis type="category" dataKey="name" width={140} />
                <Tooltip />
                <Bar
                  dataKey="value"
                  fill={CHART_COLORS[0]}
                  radius={[0, 4, 4, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>

      {/* Financial accountability + QoL radar */}
      <div className="funderDashboard-grid-3">
        <Card
          title="Financial Accountability"
          description="Key financial metrics."
        >
          <FinancialRow label="Avg. Ticket Size" value={averageTicketSize} />
          <FinancialRow
            label="Repayment Rate"
            value={repaymentRate}
            tone="success"
          />
          <FinancialRow label="Capital Leverage" value={leverageRatio} />
          <FinancialRow label="Portfolio Revenue" value={portfolioRevenue} />
        </Card>

        <Card
          title="Quality of Life Outcomes"
          description={`Aggregated indicators (avg: ${avgQoL.toFixed(1)}/5).`}
          className="funderDashboard-card-span2"
        >
          <div className="funderDashboard-chart funderDashboard-chart-md">
            <ResponsiveContainer width="100%" height="100%">
              <RadarChart
                cx="50%"
                cy="50%"
                outerRadius="70%"
                data={qolRadarData}
              >
                <PolarGrid />
                <PolarAngleAxis dataKey="indicator" />
                <PolarRadiusAxis angle={30} domain={[0, 5]} />
                <Radar
                  name="Portfolio Average"
                  dataKey="value"
                  stroke={CHART_COLORS[0]}
                  fill={CHART_COLORS[0]}
                  fillOpacity={0.4}
                />
                <Tooltip />
              </RadarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>

      {/* Portfolio sustainability */}
      <Card
        title="Portfolio Sustainability"
        description="Venture survival and growth metrics."
      >
        <div className="funderDashboard-sustain">
          <SustainTile value={survival6Months} label="6-Month Survival" />
          <SustainTile
            value={survival12Months}
            label="12-Month Survival"
            tone="primary"
          />
          <SustainTile
            value={qolImprovement}
            label="Avg. QoL Improvement"
            tone="accent"
          />
          <SustainTile
            value={graduatedCount}
            label="Graduated Ventures"
            tone="success"
          />
        </div>
      </Card>
    </div>
  );
}

/* ---------------------------------------------------------------------- *
 * Local helpers + presentational subcomponents
 * ---------------------------------------------------------------------- */

function FinancialRow({ label, value, tone }) {
  return (
    <div className="funderDashboard-finRow">
      <span className="funderDashboard-finRow-label">{label}</span>
      <span
        className={
          "funderDashboard-finRow-value" +
          (tone === "success" ? " funderDashboard-finRow-value-success" : "")
        }
      >
        {value}
      </span>
    </div>
  );
}

function SustainTile({ value, label, tone = "primary" }) {
  return (
    <div className="funderDashboard-sustainTile">
      <p
        className={`funderDashboard-sustainValue funderDashboard-sustainValue-${tone}`}
      >
        {value}
      </p>
      <p className="funderDashboard-sustainLabel">{label}</p>
    </div>
  );
}
